import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Bike, Clock, Phone, Share2 } from 'lucide-react';
import { StatusPill } from '@/components/shop/ShopUI';
import { getShipmentTracking } from '@/api/shop';
import { istDateTime } from '@/lib/shop';

/**
 * "Delivered by Kuicko": the rider carrying one shipment (docs/shop/KUICKO_DELIVERY.md
 * in the backend). While the booking is active it polls the tracking endpoint every
 * 15s and draws the rider on a small OpenStreetMap tile map (no map library).
 */

const ACTIVE = new Set(['requested', 'searching_rider', 'rider_assigned', 'rider_at_pickup', 'out_for_delivery']);

const STATUS = {
  requested: { label: 'Finding a rider', tone: 'warning' },
  searching_rider: { label: 'Finding a rider', tone: 'warning' },
  rider_assigned: { label: 'Rider on the way to the store', tone: 'info' },
  rider_at_pickup: { label: 'Rider at the store', tone: 'info' },
  out_for_delivery: { label: 'On the way to you', tone: 'info' },
  delivered: { label: 'Delivered', tone: 'success' },
  cancelled: { label: 'Rider cancelled', tone: 'muted' },
  failed: { label: 'Delivery failed', tone: 'danger' },
  error: { label: 'Rider not booked', tone: 'muted' },
};

const EVENT = {
  CREATED: 'Rider requested',
  SEARCHING_RIDER: 'Finding a rider',
  RIDER_ASSIGNED: 'Rider assigned',
  RIDER_AT_PICKUP: 'Rider reached the store',
  OUT_FOR_DELIVERY: 'Picked up, on the way to you',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  FAILED: 'Delivery failed',
};

/* ------------------------------------------------------------------ map */

const TILE = 256;
const project = (lat, lng, z) => {
  const scale = TILE * 2 ** z;
  const s = Math.sin((Math.max(-85, Math.min(85, lat)) * Math.PI) / 180);
  return { x: ((lng + 180) / 360) * scale, y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * scale };
};
const valid = (p) => p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng));

const Pin = ({ x, y, className, label, children }) => (
  <span
    className={`absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white shadow-md ${className}`}
    style={{ left: x, top: y }}
    title={label}
    aria-label={label}
  >
    {children}
  </span>
);

/** Static OSM tiles fitted around store, rider and drop; pins positioned in Web Mercator. */
export const RiderMap = ({ live, height = 200 }) => {
  const box = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  const points = [live?.pickup, live?.drop, live?.rider].filter(valid).map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
  if (!points.length) return null;

  let view = null;
  if (width > 0) {
    const pad = 36;
    let z = 17;
    for (; z > 3; z -= 1) {
      const px = points.map((p) => project(p.lat, p.lng, z));
      const w = Math.max(...px.map((p) => p.x)) - Math.min(...px.map((p) => p.x));
      const h = Math.max(...px.map((p) => p.y)) - Math.min(...px.map((p) => p.y));
      if (w <= width - pad * 2 && h <= height - pad * 2) break;
    }
    const px = points.map((p) => project(p.lat, p.lng, z));
    const cx = (Math.max(...px.map((p) => p.x)) + Math.min(...px.map((p) => p.x))) / 2;
    const cy = (Math.max(...px.map((p) => p.y)) + Math.min(...px.map((p) => p.y))) / 2;
    const left = cx - width / 2;
    const top = cy - height / 2;
    const tiles = [];
    const n = 2 ** z;
    for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + width) / TILE); tx += 1) {
      for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + height) / TILE); ty += 1) {
        if (ty < 0 || ty >= n) continue;
        tiles.push({ key: `${tx}/${ty}`, src: `https://tile.openstreetmap.org/${z}/${((tx % n) + n) % n}/${ty}.png`, x: tx * TILE - left, y: ty * TILE - top });
      }
    }
    const at = (p) => (valid(p) ? (({ x, y }) => ({ x: x - left, y: y - top }))(project(Number(p.lat), Number(p.lng), z)) : null);
    view = { tiles, store: at(live.pickup), drop: at(live.drop), rider: at(live.rider) };
  }

  return (
    <div ref={box} className="relative overflow-hidden rounded-xl bg-surface-sunken" style={{ height }} role="img" aria-label="Map with the rider's position">
      {view && (
        <>
          {view.tiles.map((t) => (
            <img key={t.key} src={t.src} alt="" draggable={false} className="absolute max-w-none select-none" style={{ left: t.x, top: t.y, width: TILE, height: TILE }} />
          ))}
          {view.store && view.drop && (
            <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
              <line x1={view.store.x} y1={view.store.y} x2={view.drop.x} y2={view.drop.y} stroke="#111827" strokeOpacity="0.55" strokeWidth="3" strokeDasharray="6 6" strokeLinecap="round" />
            </svg>
          )}
          {view.store && <Pin {...view.store} className="h-4 w-4 bg-ink-900" label="Store" />}
          {view.drop && <Pin {...view.drop} className="h-4 w-4 bg-success" label="You" />}
          {view.rider && (
            <Pin {...view.rider} className="h-8 w-8 bg-brand-secondary text-white" label="Rider">
              <Bike className="h-4 w-4" />
            </Pin>
          )}
          <div className="absolute left-2 top-2 flex gap-2 rounded-lg bg-white/90 px-2 py-1 text-[10px] font-semibold text-ink-700 shadow-sm">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-ink-900" /> Store</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-brand-secondary" /> Rider</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-success" /> You</span>
          </div>
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-0 right-0 bg-white/80 px-1.5 py-0.5 text-[9px] text-ink-700"
          >
            © OpenStreetMap contributors
          </a>
        </>
      )}
    </div>
  );
};

/* ----------------------------------------------------------------- card */

const DeliveryCard = ({ orderId, shipment, onStatusChange }) => {
  const [delivery, setDelivery] = useState(shipment.delivery);
  const [live, setLive] = useState(null);
  const status = delivery?.status;
  const active = ACTIVE.has(status);
  const changed = useRef(onStatusChange);
  changed.current = onStatusChange;

  useEffect(() => setDelivery(shipment.delivery), [shipment.delivery]);

  useEffect(() => {
    if (!active) {
      setLive(null);
      return undefined;
    }
    let stop = false;
    const tick = async () => {
      try {
        const t = await getShipmentTracking(orderId, shipment.id);
        if (stop) return;
        setLive(t?.live || null);
        if (t?.delivery) {
          setDelivery((prev) => {
            if (prev?.status !== t.delivery.status) setTimeout(() => changed.current?.(), 0);
            return t.delivery;
          });
        }
      } catch {
        /* keep the last position; the next tick retries */
      }
    };
    tick();
    const timer = setInterval(tick, 15000);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, [active, orderId, shipment.id]);

  if (!delivery) return null;
  // A booking that never happened (Kuicko couldn't take it) or was handed back: the store ships it.
  if (shipment.deliveryProvider !== 'kuicko' && (status === 'error' || status === 'cancelled')) return null;

  const meta = STATUS[status] || { label: status, tone: 'muted' };
  const rider = delivery.rider;
  const eta = live?.etaMinutes;

  const share = async () => {
    const url = delivery.trackingUrl;
    if (!url) return;
    try {
      if (navigator.share) await navigator.share({ title: 'Track my LK Shop delivery', url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success('Tracking link copied');
      }
    } catch {
      /* share sheet dismissed */
    }
  };

  return (
    <div className="rounded-2xl border border-ink-200/70 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          <Bike className="h-4 w-4 text-brand-secondary" /> Delivered by Kuicko
        </p>
        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
      </div>

      {active && eta != null && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-700">
          <Clock className="h-4 w-4 text-ink-500" />
          {status === 'out_for_delivery' ? 'Arriving in about ' : 'Picking up in about '}
          <span className="font-semibold text-ink-900">{eta} min</span>
        </p>
      )}
      {(status === 'searching_rider' || status === 'requested') && (
        <p className="mt-2 text-sm text-ink-500">The store has packed your order and Kuicko is finding a rider nearby.</p>
      )}

      {rider && (
        <div className="mt-3 flex items-center gap-3">
          {rider.photo ? (
            <img src={rider.photo} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" />
          ) : (
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-base font-bold text-ink-700">
              {rider.name?.[0]?.toUpperCase() || 'R'}
            </span>
          )}
          <div className="min-w-0 flex-1 text-sm">
            <p className="truncate font-semibold text-ink-900">{rider.name}</p>
            <p className="text-xs text-ink-500">
              {[delivery.vehicle, rider.vehicleNumber].filter(Boolean).join(' · ')}
            </p>
          </div>
          {rider.phone && active && (
            <a href={`tel:${rider.phone}`} className="lk-btn-ghost min-h-[40px] shrink-0 px-3 py-2 text-sm">
              <Phone className="h-4 w-4" /> Call rider
            </a>
          )}
        </div>
      )}

      {active && live && (
        <div className="mt-3">
          <RiderMap live={live} />
        </div>
      )}

      {delivery.timeline?.length > 0 && (
        <ol className="mt-3 space-y-1 border-t border-ink-200/70 pt-3 text-xs">
          {delivery.timeline.map((t, i) => (
            <li key={`${t.status}-${t.at}`} className={`flex justify-between gap-3 ${i === delivery.timeline.length - 1 ? 'font-semibold text-ink-900' : 'text-ink-500'}`}>
              <span>{EVENT[t.status] || t.status}</span>
              <span className="tabular shrink-0">{istDateTime(t.at)}</span>
            </li>
          ))}
        </ol>
      )}

      {delivery.trackingUrl && active && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={share} className="lk-btn-ghost min-h-[40px] px-3 py-2 text-sm">
            <Share2 className="h-4 w-4" /> Share tracking
          </button>
          <a href={delivery.trackingUrl} target="_blank" rel="noopener noreferrer" className="self-center text-xs font-semibold text-brand-secondary hover:underline">
            Open tracking page
          </a>
        </div>
      )}
    </div>
  );
};

export default DeliveryCard;
